<template>
    <div class="column-chart-template">
        <canvas :id="'chart-'+id" :class="(if_chart_has_data ? '':'hide')" class="column-chart chart-template"></canvas>
        <div :class="(!if_chart_has_data && !if_refreshing ? 'd-inline-block':'hide')" class='text-center'>
            <h1 class="text-secondary mb-1"><i class="fas fa-chart-simple"></i></h1>
            <h4 class="text-secondary mb-2"><em>Press the <strong>Preview</strong> button to render the chart</em></h4>
        </div>
    </div>
</template>

<script>
import { defineComponent, markRaw } from 'vue';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChartjsPluginStacked100 from "chartjs-plugin-stacked100";
import chartTooltip from '../../../../mixins/chart_tooltip';
import chartBase from '../../../../mixins/chart_base';

export default defineComponent({
    name: 'StackedColumnChartTemplate',
    mixins: [chartTooltip, chartBase],
    props: {
        chart_selected: {
            type: Object,
            required: true
        },
        id: {
            type: [String, Number],
            required: true
        },
        if_refreshing: {
            type: Boolean,
            default: false
        },
        if_on_report_page: {
            type: Boolean,
            default: false
        },
        if_benchmark_report: {
            type: Boolean,
            default: false
        },
        applied_chart_filter: {
            type: Object,
            default: () => ({})
        },
        if_hundred_percent: {
            type: Boolean,
            default: false
        },
        if_show_label: {
            type: Boolean,
            default: false
        },
        size: {
            type: String,
            default: 'small'
        },
        section_id: {
            type: [String, Number],
            default: null
        },
        show_compressed_numbers: {
            type: Boolean,
            default: true
        },
        forceShowAllData: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            format_type: 'number',
            color: 'yellow',
            total_values: [],
            /** When true, next chart render (e.g. after resize) uses no animation. Cleared after render. */
            skipAnimationForNextRender: false,
        };
    },
    watch: {
        applied_chart_filter: {
            handler(newVal) {
                this.if_chart_filter_applied = newVal?.if_chart_filter_applied && this.id === newVal?.section_chart_id;
                this.debouncedRender();
            },
            deep: true
        },
        size(newSize, oldSize) {
            if (newSize !== oldSize) {
                this.skipAnimationForNextRender = true;
            }
        },
    },
    methods: {

        /**
         * Number of datasets currently visible. Uses chart legend state when chart exists, else all visible.
         */
        getVisibleDatasetCount() {
            if (this.chart?.data?.datasets && typeof this.chart.isDatasetVisible === 'function') {
                let count = 0;
                for (let i = 0; i < this.chart.data.datasets.length; i++) {
                    if (this.chart.isDatasetVisible(i)) count++;
                }
                return count || 1;
            }
            return this.num_of_datasets || 1;
        },

        /**
         * Calculate how many labels (categories) to show based on chart size
         * Stacked column chart truncation logic (similar to column chart):
         * - small: show labels based on number of datasets
         * - medium: show labels based on number of datasets
         * - large/x-large: show all labels
         */
         calculateLabelsToShow() {
            const chartSize = this.size || this.chart_selected?.size || 'small';
            const totalLabels = this.chart_selected.labels?.length || 0;
            const visibleDatasets = this.getVisibleDatasetCount();
            
            if (chartSize === 'small') {
                return Math.min(2, totalLabels);
            }
            if (chartSize === 'medium') {
                return Math.min(5, totalLabels);
            }
            if (chartSize === 'large' || chartSize === 'x-large') {
                // x-large uses same chart width as large (col-4 definitions on the right)
                if (totalLabels < 15) {
                    return totalLabels;
                }
                const labelsToShow = Math.max(1, Math.floor(15 / visibleDatasets));
                return Math.min(labelsToShow, totalLabels);
            }
            return totalLabels;
        },
        
        renderChart() {
            if (!this.chart_selected?.data) return;

            // Set pagination and dataset state early for accurate calculations
            this.num_of_datasets = Object.keys(this.chart_selected.data).length;
            this.totalDataLength = this.chart_selected.labels?.length || 0;
            this.displayedDataCount = this.forceShowAllData ? this.totalDataLength : this.calculateLabelsToShow();
            this.isDataTruncated = this.displayedDataCount < this.totalDataLength;

            // Emit state to parent for centralized pagination controls
            this.emitPaginationState();

            // Get full dataset first (will use displayedDataCount for styling)
            const fullDataset = this.getChartData();
            
            // Apply truncation using base mixin method
            const dataset = this.getTruncatedData(fullDataset);
            const options = this.getChartOptions(dataset);

            // First destroy any existing chart
            this.destroyChart();

            // Wait for the next tick to ensure DOM is ready and previous chart is fully destroyed
            this.$nextTick(() => {
                const ctx = document.getElementById('chart-' + this.id);
                if (ctx?.getContext) {
                    try {
                        this.createChartWhenVisible(ctx, options, (chartInstance) => {
                            this.chart = markRaw(chartInstance);
                            this.setupChartMouseLeave(chartInstance);
                            this.skipAnimationForNextRender = false;
                        });
                    } catch (error) {
                        console.error('Error creating chart:', error);
                    }
                }
            });
        },
        debouncedRender() {
            if (this.render_timeout) {
                clearTimeout(this.render_timeout);
            }
            this.render_timeout = setTimeout(() => {
                this.$nextTick(() => {
                    this.format_type = this.chart_selected?.metric_formats?.[0] || 'number';
                    this.color = this.chart_selected?.metrics?.[0]?.color || 'yellow';
                    this.num_of_datasets = Object.keys(this.chart_selected.data).length;
                    this.if_chart_has_data = true;
                    this.renderChart();
                    this.emitPaginationState();
                });
            }, 250); // 250ms debounce
        },
        getBorderRadiusByDataLength(dataLength) {
            if (dataLength < 5) {
                return 10;
            } else if (dataLength < 10) {
                return 7;
            } else if (dataLength < 15) {
                return 5;
            } else {
                return 3;
            }
        },
        /**
         * Calculates a nice rounded-up maximum value for the y-axis
         * Logic: if (top_axis - highest_bar) < 35% of the space between axis ticks, add additional top axis
         */
        getYAxisMax(maxValue) {
            if (maxValue <= 0) {
                return 1;
            }
            
            // Calculate the order of magnitude
            const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
            const normalized = maxValue / magnitude;
            
            // Calculate natural top axis (round up to next nice number)
            let naturalTop;
            if (normalized <= 1) {
                naturalTop = 1 * magnitude;
            } else if (normalized <= 1.5) {
                naturalTop = 1.5 * magnitude;
            } else if (normalized <= 2) {
                naturalTop = 2 * magnitude;
            } else if (normalized <= 2.5) {
                naturalTop = 2.5 * magnitude;
            } else if (normalized <= 4) {
                naturalTop = 4 * magnitude;
            } else if (normalized <= 5) {
                naturalTop = 5 * magnitude;
            } else if (normalized <= 10) {
                naturalTop = 10 * magnitude;
            } else {
                naturalTop = magnitude * 10;
            }
            
            // Calculate gap between natural top and highest bar
            const gap = naturalTop - maxValue;
            
            // Calculate 35% of the space between axis ticks
            // For maxTicksLimit: 5, the interval is approximately naturalTop / 4
            const interval = naturalTop / 4;
            const threshold = interval * 0.35;
            
            // If gap is less than 35% of interval, add additional top axis
            if (gap < threshold) {
                // Get next level up
                const naturalTopNormalized = naturalTop / magnitude;
                if (naturalTopNormalized === 1) {
                    return 1.5 * magnitude;
                } else if (naturalTopNormalized === 1.5) {
                    return 2 * magnitude;
                } else if (naturalTopNormalized === 2) {
                    return 2.5 * magnitude;
                } else if (naturalTopNormalized === 2.5) {
                    return 4 * magnitude;
                } else if (naturalTopNormalized === 4) {
                    return 5 * magnitude;
                } else if (naturalTopNormalized === 5) {
                    return 10 * magnitude;
                } else {
                    return naturalTop * 2;
                }
            } else {
                // Gap is sufficient, use natural top axis
                return naturalTop;
            }
        },
        getChartData() {
            var datasetArray = [];
            var data_length = Object.values(this.chart_selected.data)[0].length;
            const borderRadius = this.getBorderRadiusByDataLength(data_length);
            const barThickness = this.getBarThickness(data_length);
            var max = 0;
            var totals = [];

            // Get color indices based on number of datasets
            const colorIndices = this.getColorIndicesForDatasets(this.num_of_datasets);
            const colorArray = this.getColor(this.color);
            // Map indices to actual color values
            const chartColors = colorIndices.map(idx => colorArray[idx]);

            if (this.num_of_datasets > 1) {
                // multiple dataset
                if (this.chart_filter_label.length > 1) { // multiple dimension, should only highlight the selected column within the group
                    this.chart_filter_label_index[0] = this.chart_selected.labels.findIndex(label => (label || '(not set)') == this.chart_filter_label[0]);
                    this.chart_filter_label_index[1] = Object.keys(this.chart_selected.data).findIndex(data => (data || '(not set)') == this.chart_filter_label[1]);
                } else { //single dimension, should highlight the whole selected column
                    this.chart_filter_label_index[0] = this.chart_selected.labels.findIndex(label => (label || '(not set)') == this.chart_filter_label[0]);
                }

                //Calculate the totals for the different columns
                Object.keys(this.chart_selected.data).forEach((key, index) => {
                    for (var i = 0; i < this.chart_selected.data[key].length; i++) {
                        var temp = this.chart_selected.data[key][i];
                        if (totals[i] == undefined)
                            totals.push(temp);
                        else
                            totals[i] += temp;
                    }
                })
                //Find the max value in the totals
                max = Math.max(...totals);

                Object.keys(this.chart_selected.data).forEach((key, index) => {
                    const datasetLabel = key === "" ? '(not set)' : key;
                    // Get color for this dataset (reverse order: last dataset gets first color)
                    const datasetColorIndex = chartColors.length - 1 - index;
                    const datasetColor = chartColors[datasetColorIndex] || chartColors[0];

                    if (this.if_chart_filter_applied) {
                        if (this.chart_filter_label.length > 1) {
                            var backgroundColorArray = Array(this.chart_selected.data[key].length).fill(this.getColorByLetter('gray', 'D'));
                            if (datasetLabel == this.chart_filter_label[1]) {
                                backgroundColorArray[this.chart_filter_label_index[0]] = chartColors[0]; // Use color A
                            }
                            datasetArray.push({
                                label: datasetLabel,
                                data: this.chart_selected.data[key],
                                backgroundColor: backgroundColorArray,
                                borderWidth: 0,
                                borderRadius: borderRadius,
                                maxBarThickness: barThickness,
                            });
                        } else {
                            var backgroundColorArray = Array(this.chart_selected.data[key].length).fill(this.getColorByLetter('gray', 'D'));
                            backgroundColorArray[this.chart_filter_label_index[0]] = chartColors[0]; // Use color A
                            datasetArray.push({
                                label: datasetLabel,
                                data: this.chart_selected.data[key],
                                backgroundColor: backgroundColorArray,
                                borderWidth: 0,
                                borderRadius: borderRadius,
                                maxBarThickness: barThickness,
                            });
                        }
                    } else if (this.chart_selected.highlight_last_value) {
                        const isLastDataset = index == this.num_of_datasets - 1;
                        let backgroundColor;
                        if (isLastDataset) {
                            backgroundColor = chartColors[0]; // Use color A
                        } else {
                            // Map index to gray color (reverse: index 0 -> G, index 1 -> F, etc.)
                            const grayIndex = Math.max(0, Math.min(6 - index - 1, 6));
                            const grayLetter = ['G', 'F', 'E', 'D', 'C', 'B', 'A'][grayIndex] || 'A';
                            backgroundColor = this.getColorByLetter('gray', grayLetter);
                        }
                        datasetArray.push({
                            label: datasetLabel,
                            data: this.chart_selected.data[key],
                            backgroundColor: backgroundColor,
                            borderWidth: 0,
                            borderRadius: borderRadius,
                            maxBarThickness: barThickness,
                        });
                    } else {
                        datasetArray.push({
                            label: datasetLabel,
                            data: this.chart_selected.data[key],
                            backgroundColor: datasetColor,
                            borderWidth: 0,
                            borderRadius: borderRadius,
                            maxBarThickness: barThickness,
                        });
                    }
                });
            } else {
                // single dataset - use color A
                const chartColor = this.getColorByLetter(this.color, 'A');

                Object.keys(this.chart_selected.data).forEach(key => {
                    const datasetLabel = key === "" ? '(not set)' : key;
                    var max_temp = Math.max(...this.chart_selected.data[key]);
                    if (max_temp > max) {
                        max = max_temp;
                    }
                    var backgroundColorArray = [];
                    if (this.if_chart_filter_applied) {
                        var index = this.chart_selected.labels.findIndex(label => (label || '(not set)') == this.chart_filter_label[0]);
                        backgroundColorArray = Array(this.chart_selected.data[key].length).fill(this.getColorByLetter('gray', 'D'));
                        backgroundColorArray[index] = chartColor;
                    } else if (this.chart_selected.highlight_last_value) {
                        backgroundColorArray = Array(this.chart_selected.data[key].length - 1).fill(this.getColorByLetter('gray', 'D'));
                        backgroundColorArray.push(chartColor);
                    }
                    else {
                        backgroundColorArray = Array(this.chart_selected.data[key].length).fill(chartColor);
                    }
                    datasetArray.push({
                        label: datasetLabel,
                        data: this.chart_selected.data[key],
                        backgroundColor: backgroundColorArray,
                        borderWidth: 0,
                        borderRadius: borderRadius,
                        barThickness: barThickness,
                    });
                });
            }

            var labels_temp = [];
            this.chart_selected.labels.forEach((label) => {
                labels_temp.push(label || '(not set)');
            });
            this.max_value = max;
            this.total_values = totals;

            return {
                labels: labels_temp,
                datasets: datasetArray
            };
        },
        getChartOptions(data) {
            var self = this;
            
            // Custom plugin to draw background color for hovered group
            const hoverBackgroundPlugin = {
                id: 'hoverBackground',
                beforeDatasetsDraw: (chart) => {
                    if (self.hoveredIndex !== null && chart.scales.x) {
                        const ctx = chart.ctx;
                        const xScale = chart.scales.x;
                        const yScale = chart.scales.y;
                        
                        // Get shade E of the metric color with 60% opacity
                        const colorE = self.getColorByLetter(self.color, 'E');
                        const rgbMatch = colorE.match(/rgb\((\d+)\s+(\d+)\s+(\d+)\)/);
                        
                        if (rgbMatch) {
                            const r = rgbMatch[1];
                            const g = rgbMatch[2];
                            const b = rgbMatch[3];
                            const rgbaColor = `rgba(${r}, ${g}, ${b}, ${0.3 * self.hoverOpacity})`;
                            
                            // Get the pixel positions for the hovered category and adjacent categories
                            const hoveredPixelValue = xScale.getPixelForValue(self.hoveredIndex);
                            const totalWidth = xScale.width;
                            const numCategories = xScale.ticks.length;
                            const categorySpacing = totalWidth / numCategories;
                            
                            // Calculate left edge: midpoint between previous category and hovered category
                            let leftEdge;
                            if (self.hoveredIndex > 0) {
                                const prevPixelValue = xScale.getPixelForValue(self.hoveredIndex - 1);
                                leftEdge = (prevPixelValue + hoveredPixelValue) / 2;
                            } else {
                                // For first category, use midpoint between start and current
                                leftEdge = hoveredPixelValue - categorySpacing / 2;
                            }
                            
                            // Calculate right edge: midpoint between hovered category and next category
                            let rightEdge;
                            if (self.hoveredIndex < numCategories - 1) {
                                const nextPixelValue = xScale.getPixelForValue(self.hoveredIndex + 1);
                                rightEdge = (hoveredPixelValue + nextPixelValue) / 2;
                            } else {
                                // For last category, use midpoint between current and end
                                rightEdge = hoveredPixelValue + categorySpacing / 2;
                            }
                            
                            // Clamp to chart area so overlay never covers y-axis or x-axis labels
                            const chartLeft = chart.chartArea.left;
                            const chartRight = chart.chartArea.right;
                            const leftEdgeClipped = Math.max(leftEdge, chartLeft);
                            const rightEdgeClipped = Math.min(rightEdge, chartRight);
                            
                            // Calculate x position and width for the wider highlight box
                            const x = leftEdgeClipped;
                            const categoryWidth = rightEdgeClipped - leftEdgeClipped;
                            const y = yScale.top;
                            
                            // Extend height to include axis labels
                            // Get the bottom of the x-axis scale which includes the label area
                            const xAxisBottom = xScale.bottom || chart.chartArea.bottom;
                            const height = xAxisBottom - yScale.top;
                            
                            // Draw rounded rectangle with 30px border radius
                            ctx.save();
                            ctx.fillStyle = rgbaColor;
                            const radius = 30;
                            ctx.beginPath();
                            ctx.moveTo(x + radius, y);
                            ctx.lineTo(x + categoryWidth - radius, y);
                            ctx.quadraticCurveTo(x + categoryWidth, y, x + categoryWidth, y + radius);
                            ctx.lineTo(x + categoryWidth, y + height - radius);
                            ctx.quadraticCurveTo(x + categoryWidth, y + height, x + categoryWidth - radius, y + height);
                            ctx.lineTo(x + radius, y + height);
                            ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
                            ctx.lineTo(x, y + radius);
                            ctx.quadraticCurveTo(x, y, x + radius, y);
                            ctx.closePath();
                            ctx.fill();
                            ctx.restore();
                        }
                    }
                }
            };
            
            // Custom plugin to darken hovered bars
            // Uses afterDatasetsDraw (not afterDraw) so datalabels plugin draws on top when hovering
            const darkenBarsPlugin = {
                id: 'darkenBars',
                afterDatasetsDraw: (chart) => {
                    if (self.hoveredIndex !== null) {
                        const ctx = chart.ctx;
                        
                        // Iterate through all datasets
                        chart.data.datasets.forEach((dataset, datasetIndex) => {
                            const meta = chart.getDatasetMeta(datasetIndex);
                            if (!meta || meta.hidden) return;
                            
                            // Get the bar element for the hovered index
                            const bar = meta.data[self.hoveredIndex];
                            if (!bar) return;
                            
                            // Get the original color
                            const originalColor = Array.isArray(dataset.backgroundColor)
                                ? dataset.backgroundColor[self.hoveredIndex]
                                : dataset.backgroundColor;
                            
                            // Darken the color by 10%
                            const darkerColor = self.darkenColor(originalColor, 0.1);
                            
                            // Get bar properties
                            const { x, y, base, width, height } = bar.getProps(['x', 'y', 'base', 'width', 'height']);
                            
                            // Get border radius from the dataset
                            const borderRadius = dataset.borderRadius || 0;
                            
                            // Calculate actual border radius value
                            const actualRadius = Math.min(borderRadius, width / 2, Math.abs(height) / 2);
                            
                            // Draw the darkened bar on top
                            ctx.save();
                            ctx.fillStyle = darkerColor;
                            
                            // Draw rounded rectangle (only top corners rounded for stacked bars)
                            const barHeight = y - base;
                            const barY = Math.min(y, base);
                            const barX = x - width / 2;
                            const barBottom = barY + Math.abs(barHeight);
                            
                            ctx.beginPath();
                            // Start at top-left (rounded)
                            ctx.moveTo(barX + actualRadius, barY);
                            // Top edge
                            ctx.lineTo(barX + width - actualRadius, barY);
                            // Top-right corner (rounded)
                            ctx.quadraticCurveTo(barX + width, barY, barX + width, barY + actualRadius);
                            // Right edge (straight to bottom)
                            ctx.lineTo(barX + width, barBottom);
                            // Bottom-right corner (sharp)
                            ctx.lineTo(barX, barBottom);
                            // Bottom-left corner (sharp)
                            ctx.lineTo(barX, barY + actualRadius);
                            // Top-left corner (rounded)
                            ctx.quadraticCurveTo(barX, barY, barX + actualRadius, barY);
                            ctx.closePath();
                            ctx.fill();
                            ctx.restore();
                        });
                    }
                }
            };
            
            // Custom plugin to draw side labels for all segments when hovering
            const sideLabelsPlugin = {
                id: 'sideLabels',
                afterDraw: (chart) => {
                    if (self.hoveredIndex !== null && self.hoverOpacity > 0) {
                        const ctx = chart.ctx;
                        const xScale = chart.scales.x;
                        const yScale = chart.scales.y;
                        
                        // Get the first visible bar to determine bar width and position
                        let firstBar = null;
                        for (let datasetIndex = 0; datasetIndex < chart.data.datasets.length; datasetIndex++) {
                            const meta = chart.getDatasetMeta(datasetIndex);
                            if (meta && !meta.hidden) {
                                const bar = meta.data[self.hoveredIndex];
                                if (bar) {
                                    firstBar = bar;
                                    break;
                                }
                            }
                        }
                        
                        if (!firstBar) return;
                        
                        const { x: barX, width: barWidth } = firstBar.getProps(['x', 'width']);
                        
                        // Calculate spacing for labels on the side (from right edge of bar)
                        const spacing = 15; // Space from the right edge of the bar
                        const labelX = barX + (barWidth / 2) + spacing;
                        
                        // Iterate through all datasets to show labels for each segment
                        const datasets = chart.data.datasets;
                        for (let datasetIndex = 0; datasetIndex < datasets.length; datasetIndex++) {
                            const dataset = datasets[datasetIndex];
                            const meta = chart.getDatasetMeta(datasetIndex);
                            
                            if (!meta || meta.hidden) continue;
                            
                            const bar = meta.data[self.hoveredIndex];
                            if (!bar) continue;
                            
                            const { y, base, height } = bar.getProps(['y', 'base', 'height']);
                            
                            // Calculate the middle y position of this segment
                            const segmentTop = Math.min(y, base);
                            const segmentBottom = segmentTop + Math.abs(height);
                            const segmentMiddle = (segmentTop + segmentBottom) / 2;
                            
                            // Get the value for this segment
                            const value = dataset.data[self.hoveredIndex];
                            
                            // Format the label
                            let labelText = '';
                            if(self.chart_selected.show_series_percentage){
                                labelText = self.percentage(value);
                            } else {
                                if(self.format_type == 'money') {
                                    labelText = self.show_compressed_numbers && value >= 1000 
                                        ? self.currency_abbr(value) 
                                        : self.currency_with_zero(value);
                                } else if(self.format_type == 'number') {
                                    if(self.show_compressed_numbers && value >= 1000) {
                                        labelText = self.num_abbr(value);
                                    } else if(self.max_value >= 10) {
                                        labelText = self.number_with_zero(value);
                                    } else {
                                        labelText = self.number_with_decimal(value);
                                    }
                                } else if(self.format_type == 'percent') {
                                    labelText = self.percentage(value);
                                } else {
                                    labelText = value.toString();
                                }
                            }
                            
                            // Draw the label
                            ctx.save();
                            ctx.font = `${self.font_size}px 'Nunito', sans-serif`;
                            ctx.fillStyle = `rgba(17, 25, 33, ${self.hoverOpacity})`;
                            ctx.textAlign = 'left';
                            ctx.textBaseline = 'middle';
                            ctx.fillText(labelText, labelX, segmentMiddle);
                            ctx.restore();
                        }
                    }
                }
            };

            // Custom plugin to draw solid line at y=0 (base x-axis) extending across chart area including x-axis labels
            const solidBaseLinePlugin = {
                id: 'solidBaseLine',
                afterDraw: (chart) => {
                    const ctx = chart.ctx;
                    const yScale = chart.scales.y;
                    const xScale = chart.scales.x;
                    
                    // Get the y position for zero
                    const zeroY = yScale.getPixelForValue(0);
                    
                    // Use chart area boundaries and extend slightly to cover x-axis labels
                    // Start from the left edge of the chart area (where bars start)
                    // End at the right edge of the chart area, extending slightly to cover labels
                    const leftEdge = chart.chartArea.left;
                    const rightEdge = xScale.right; // Extend to cover x-axis labels
                    
                    // Draw solid line extending across the chart area and x-axis labels
                    ctx.save();
                    ctx.strokeStyle = 'rgba(206, 219, 215, 1)';
                    ctx.lineWidth = 1;
                    ctx.setLineDash([]); // Solid line - empty array means no dashes
                    ctx.beginPath();
                    ctx.moveTo(leftEdge, zeroY);
                    ctx.lineTo(rightEdge, zeroY);
                    ctx.stroke();
                    ctx.restore();
                }
            };
            
            var config = {
                plugins: this.if_hundred_percent 
                    ? [ChartjsPluginStacked100, solidBaseLinePlugin, hoverBackgroundPlugin, darkenBarsPlugin, ChartDataLabels, sideLabelsPlugin]
                    : [solidBaseLinePlugin, hoverBackgroundPlugin, darkenBarsPlugin, ChartDataLabels, sideLabelsPlugin],
                type: 'bar',
                data: data,

                //All of the special aspects that make these charts unique
                options: {
                    // onClick: (e) => {
                    //     const canvasPosition = getRelativePosition(e, this.chart);

                    //     // Substitute the appropriate scale IDs
                    //     const dataX = this.chart.scales.x.getValueForPixel(canvasPosition.x);
                    //     const dataY = this.chart.scales.y.getValueForPixel(canvasPosition.y);
                    //     console.log(dataX, dataY);
                    // },
                    interaction: {
                        intersect: false,
                        mode: 'index',
                    },
                    onHover: (event, activeElements, chart) => {
                        if (activeElements && activeElements.length > 0) {
                            // Clear any pending hide timeout when hovering over an element
                            if (this.hideTimeout) {
                                clearTimeout(this.hideTimeout);
                                this.hideTimeout = null;
                            }
                            
                            const activeElement = activeElements[0];
                            const dataIndex = activeElement.index;

                            // If hovering over a different data point OR returning to a point that's not currently hovered,
                            // (re)start the debounce timer
                            if (dataIndex !== this.pendingHoverIndex || this.hoveredIndex !== dataIndex) {
                                // Cancel any pending show timeout
                                if (this.hoverTimeout) {
                                    clearTimeout(this.hoverTimeout);
                                }
                                
                                // Reset current state immediately when moving to a new point
                                // to keep highlight and tooltip in sync (both hidden during the debounce)
                                if (this.hoveredIndex !== null && this.hoveredIndex !== dataIndex) {
                                    this.hoveredIndex = null;
                                    this.hoverOpacity = 0;
                                    chart.update('none');
                                }
                                
                                // Reset flags for new data point
                                this.pendingHoverIndex = dataIndex;
                                
                                // Start new debounce timer - 200ms delay to show hover
                                this.hoverTimeout = setTimeout(() => {
                                    this.hoverTimeout = null;
                                    // Apply hover effect after delay
                                    if (this.hoveredIndex !== dataIndex) {
                                        this.hoveredIndex = dataIndex;
                                        chart.update('none'); // Immediate update for bar colors and tooltip
                                        this.animateHoverOpacity(1, chart);
                                    }
                                }, 200);
                            }
                        } else {
                            // Clear any pending show timeout
                            if (this.hoverTimeout) {
                                clearTimeout(this.hoverTimeout);
                                this.hoverTimeout = null;
                                this.pendingHoverIndex = null;
                            }

                            // Debounce hiding the hover state (200ms delay)
                            // This matches the show debounce timing for consistency
                            if (this.hoveredIndex !== null && !this.hideTimeout) {
                                this.hideTimeout = setTimeout(() => {
                                    // Only hide if still not hovering and tooltip is also not being hovered
                                    if (this.hoveredIndex !== null && !this.isTooltipHovered) {
                                        this.hoveredIndex = null;
                                        this.animateHoverOpacity(0, chart);
                                    }
                                    this.hideTimeout = null;
                                }, 200);
                            }
                        }
                    },
                    animation: {
                        duration: this.skipAnimationForNextRender ? 0 : 1000,
                    },
                    responsive: true,
                    animations: {
                        x: {
                            duration: 1000,
                        }
                    },
                    maintainAspectRatio: false,
                    datasets: {
                        bar: {
                            barPercentage: 0.6,
                            categoryPercentage: 0.8,
                        }
                    },
                    layout: {
                        padding: {
                            top: 30,
                            right: 30,
                            bottom: 10
                        }
                    },
                    indexAxis: 'x',
                    scales: {
                        x: {
                            //If the type is bar-stacked, it will make it a stacked bar chart
                            stacked: true,
                            grid: {
                                drawBorder: false,
                                display : false
                            },
                            border: {
                                display: false,
                            },
                            ticks: {
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: "'Nunito', sans-serif",
                                },
                                maxRotation:0,
                                autoSkip:false,
                                callback: function(value, index, ticks, context) {
                                    // Get the label text from the truncated data (data.labels contains the sliced labels)
                                    let labelText = '';
                                    if(data.labels && data.labels[value]){
                                        labelText = data.labels[value] + "";
                                    } else {
                                        labelText = '(not set)';
                                    }
                                    
                                    // Break label into multiple lines to prevent collisions
                                    // Maximum characters per line (adjust based on font size and chart width)
                                    const maxCharsPerLine = 12;
                                    const words = labelText.split(" ");
                                    const lines = [];
                                    let currentLine = '';
                                    
                                    words.forEach((word, wordIndex) => {
                                        // If adding this word would exceed the limit, start a new line
                                        const testLine = currentLine ? currentLine + ' ' + word : word;
                                        
                                        if (testLine.length > maxCharsPerLine && currentLine) {
                                            // Save current line and start a new one
                                            lines.push(currentLine);
                                            currentLine = word;
                                        } else {
                                            // Add word to current line
                                            currentLine = testLine;
                                        }
                                        
                                        // If this is the last word, add the current line
                                        if (wordIndex === words.length - 1 && currentLine) {
                                            lines.push(currentLine);
                                        }
                                    });
                                    
                                    // Return array of lines (Chart.js will render each as a separate line)
                                    // If only one line, return as single-element array for consistency
                                    return lines.length > 0 ? lines : [labelText];
                                }
                            }
                        },
                        y: {
                            display: true,
                            position: 'left',
                            beginAtZero: true,
                            stacked: true,
                            // For 100% charts, let the stacked100 plugin control the 0-100 scale
                            ...(this.if_hundred_percent ? {} : { max: this.getYAxisMax(this.max_value) }),
                            //For the ticks on the axes, format the numbers to make the pretty
                            border: {
                                display: false,
                                width: 2,
                                dash: [5,5],
                                dashOffset: 0,
                            },
                            grid: {
                                drawOnChartArea: true,
                                drawBorder: false,

                                color: 'rgba(206, 219, 215, 1)',
                                lineWidth: 1, // gridline thickness
                                borderDash: [2,2], // gridline in the chart area that is perpendicular to this axis

                                drawTicks: true,
                                tickColor: 'rgba(206, 219, 215, 1)',
                                tickLength: 20,
                                tickBorderDash: [2,2],
                                tickBorderDashOffset: 0,
                                z: -2,
                            },
                            ticks: {
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: "'Nunito', sans-serif",
                                },
                                autoSkip: true,
                                maxTicksLimit: 5,
                                callback: function(value, index, ticks, context) {
                                    if(self.if_hundred_percent)
                                        return self.number_with_decimal(value) + '%';
                                    if(self.format_type == 'money') {
                                        if(self.show_compressed_numbers && value >= 1000) {
                                            return self.currency_abbr(value);
                                        }
                                        return self.currency_with_zero(value);
                                    }
                                    if(self.format_type == 'number') {
                                        if(self.show_compressed_numbers && value >= 1000) {
                                            return self.num_abbr(value);
                                        }
                                        if(self.max_value >= 10) {
                                            return self.number_with_zero(value);
                                        }
                                        return self.number_with_decimal(value);
                                    }
                                    if(self.format_type == 'percent')
                                        return self.percentage(value);
                                }
                            }
                        }
                    },
                    plugins: {
                        legend: {
                            //Only show the legend if there is more than one media type
                            position: (data.datasets.length > 1) ? 'bottom' : 'none',
                            align: 'center',
                            fullSize: true,
                            onClick: (e, legendItem, legend) => {
                                const chart = legend.chart;
                                const datasetIndex = legendItem.datasetIndex;

                                // toggle dataset visibility
                                chart.setDatasetVisibility(datasetIndex, !chart.isDatasetVisible(datasetIndex));
                                chart.update();
                            },
                            onHover: function(e, chart) {
                                e.native.target.style.cursor = 'pointer';
                            },
                            onLeave: function(e, chart) {
                                e.native.target.style.cursor = 'default';
                            },
                            labels: {
                                usePointStyle: true,
                                pointStyle: 'circle',
                                padding: 20,
                                generateLabels: (chart) => {
                                    return chart.data.datasets.map((dataset, index) => {
                                        const fill = Object.keys(this.chart_selected.data).length > 1
                                        ? dataset.backgroundColor
                                        : dataset.backgroundColor.slice(-1);

                                        return {
                                            text: dataset.label,
                                            datasetIndex: index,
                                            hidden: !chart.isDatasetVisible(index),
                                            pointStyle: 'circle',
                                            fillStyle: fill,
                                            lineWidth: 0,
                                        };
                                    }); 
                                },
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: "'Nunito', sans-serif",
                                },
                            }
                        },
                        tooltip:  {
                            enabled: false,
                            external: function(context) {
                                const chartCanvasId = context.chart?.canvas?.id;
                                if (!chartCanvasId || !chartCanvasId.startsWith('chart-')) return;

                                const chartIdFromCanvas = chartCanvasId.replace(/^chart-/, '');
                                const expectedTooltipId = self.getTooltipId();
                                if (chartIdFromCanvas !== String(self.id)) {
                                    let tooltipEl = document.getElementById(expectedTooltipId);
                                    if (tooltipEl) {
                                        tooltipEl.style.opacity = 0;
                                        tooltipEl.style.pointerEvents = 'none';
                                    }
                                    return;
                                }

                                let tooltipEl = self.getOrCreateTooltipElement();
                                if (!tooltipEl) return;

                                const tooltipIdPrefix = 'chart-' + chartIdFromCanvas + '-';
                                if (!tooltipEl.id.startsWith(tooltipIdPrefix) || !tooltipEl.id.endsWith('-tooltip')) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    return;
                                }
                                if (tooltipEl.id !== expectedTooltipId) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    return;
                                }

                                const tooltipModel = context.tooltip;

                                if (!tooltipModel || tooltipModel.opacity === 0) {
                                    if (!self.isTooltipHovered && self.hoveredIndex === null && tooltipEl) {
                                        tooltipEl.style.opacity = 0;
                                        tooltipEl.style.pointerEvents = 'none';
                                        self.hideAllChartTooltips();
                                    }
                                    return;
                                }
                                
                                const currentDataIndex = tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0 
                                    ? tooltipModel.dataPoints[0].dataIndex 
                                    : null;
                                
                                if (currentDataIndex === null || currentDataIndex !== self.hoveredIndex) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    self.hideAllChartTooltips();
                                    return;
                                }

                                // Determine which group is hovered for smart positioning
                                const dataIndex = currentDataIndex;

                                // Hide tooltips from other sections using mixin method
                                self.hideOtherTooltips(tooltipEl);

                                // Apply base styles using mixin method
                                self.applyTooltipBaseStyles(tooltipEl);

                                // Set Text
                                if (tooltipModel.body) {
                                    const titleLines = tooltipModel.title || [];
                                    const bodyLines = tooltipModel.body.map(b => b.lines);

                                    let innerHtml = '<thead>';
                                    innerHtml += '<tr><th style="font-weight: 500; font-size: 18px; padding-bottom: 8px;">' + titleLines[0] + '</th></tr>';
                                    innerHtml += '</thead><tbody>';

                                    let tooltipBody = '';

                                    bodyLines.forEach((body, i) => {
                                        if (!body || !body[0]) return;
                                        
                                        const body_label_temp = body[0].split(':');
                                        const body_label = body_label_temp.slice(0, body_label_temp.length - 1).join(':');
                                        const value = tooltipModel.dataPoints[i].raw;

                                        const colors = tooltipModel.labelColors[i];
                                        const textColor = colors.backgroundColor;

                                        let formattedValue = '';
                                        let percentageText = '';
                                        let label = '';

                                        if(self.if_hundred_percent){
                                            if(self.format_type === 'money' && self.max_value >= 10)
                                                label = body_label + ': ' + self.currency_with_zero(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' <em>('+ value  +'%)</em>';
                                            if(self.format_type === 'money' && self.max_value < 10)
                                                label = body_label + ': ' + self.currency_with_decimal(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' <em>('+ value  +'%)</em>';
                                            if(self.format_type === 'number' && self.max_value >= 10)
                                                label = body_label + ': ' + self.number_with_zero(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' <em>('+ value +'%)</em>';
                                            if(self.format_type === 'number' && self.max_value < 10)
                                                label = body_label + ': ' + self.number_with_decimal(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' <em>('+ value +'%)</em>';
                                            if(self.format_type === 'percent')
                                                label = body_label + ': ' + self.percentage(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' <em>('+ value+'%)</em>';
                                        } else{
                                            if(self.format_type === 'money' && self.max_value >= 10)
                                                label = body_label + ': ' + self.currency_with_zero(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' <em>('+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex])  +')</em>';
                                            if(self.format_type === 'money' && self.max_value < 10)
                                                label = body_label + ': ' + self.currency_with_decimal(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' <em>('+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex])  +')</em>';
                                            if(self.format_type === 'number' && self.max_value >= 10)
                                                label = body_label + ': ' + self.number_with_zero(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' <em>('+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex]) +')</em>';
                                            if(self.format_type === 'number' && self.max_value < 10)
                                                label = body_label + ': ' + self.number_with_decimal(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' <em>('+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex]) +')</em>';
                                            if(self.format_type === 'percent')
                                                label = body_label + ': ' + self.percentage(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]) + ' <em>('+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex])+')</em>';
                                        }
                                        if(self.if_benchmark_report){
                                            tooltipBody =  '<tr><td class="d-flex align-items-center">'+ colorDiv + '<span class="me-1">' + label + '</span>' + '</td></tr>' + tooltipBody; // stacked tooltip loads in reverse order
                                        } else {
                                            if(self.format_type === 'money') {
                                                formattedValue = self.currency_with_zero(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]);
                                            } else if(self.format_type === 'number' && self.max_value >= 10) {
                                                formattedValue = self.number_with_zero(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]);
                                            } else if(self.format_type === 'number' && self.max_value < 10) {
                                                formattedValue = self.number_with_decimal(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]);
                                            } else if(self.format_type === 'percent') {
                                                formattedValue = self.percentage(context.tooltip.dataPoints[i].dataset.data[context.tooltip.dataPoints[i].dataIndex]);
                                            }
                                            percentageText = ' <em>('+ self.percentage(value / self.total_values[context.tooltip.dataPoints[i].dataIndex])  +')</em>';
                                        }

                                        if(self.if_benchmark_report){
                                            tooltipBody = '<tr><td class="d-flex align-items-center" style="padding-bottom: 4px;"><span style="color: ' + textColor + '; font-weight: 400;">' + body_label + '</span>: <span style="color: #111921; font-weight: 400; margin-left: 4px;">' + formattedValue + percentageText + '</span></td></tr>' + tooltipBody;
                                        } else {
                                            tooltipBody = '<tr><td class="d-flex align-items-center" style="padding-bottom: 4px;"><span style="color: ' + textColor + '; font-weight: 400;">' + body_label + '</span>: <span style="color: #111921; font-weight: 400; margin-left: 4px;">' + formattedValue + percentageText + '</span><button id="'+ i +'" class="btn btn-sm btn-white tooltip-btn-white btn-explore-individual p-0 ms-2" style="width:24px !important; height: 24px !important; border-radius: 50% !important; display: flex !important; align-items: center !important; justify-content: center !important;"><img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" style="width: 14px; height: 14px; margin: 0 !important;" alt=""></button></td></tr>' + tooltipBody;
                                        }
                                    });
                                    innerHtml += tooltipBody;

                                    if(self.if_on_report_page && !self.if_benchmark_report){
                                        const isFilterApplied = self.if_chart_filter_applied && self.chart_filter_label[0] == titleLines[0];

                                        innerHtml += '<div class="d-flex justify-content-end mt-2">' +
                                            '<button class="btn btn-sm btn-primary btn-explore-all d-flex align-items-center px-4" style="font-weight: 500;">' +
                                            '<img class="icon me-1" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">' +
                                            '<span class="mx-2">' + (isFilterApplied ? 'Remove as Filter' : 'Filter') + '</span>' +
                                            '</button>' +
                                            '</div>';

                                        innerHtml += '</tbody>';

                                        let tableRoot = tooltipEl.querySelector('table');
                                        tableRoot.innerHTML = innerHtml;

                                        let btnExploreIndividuals = tooltipEl.querySelectorAll('.btn-explore-individual');
                                        if (self.if_chart_filter_applied){
                                            btnExploreIndividuals.forEach((btn)=>{
                                                btn.hidden = true;
                                            })
                                        } else {
                                            btnExploreIndividuals.forEach((btn)=>{
                                                btn.hidden = false;
                                                btn.addEventListener('click', (event) => {
                                                    self.if_chart_filter_applied = true;
                                                    self.chart_filter_label = [];

                                                    var filter_array = [];
                                                    self.chart_selected.group_by.forEach((dimension, index) => {
                                                        // Multi-dimensional filter logic from StackedColumnChartTemplate.vue
                                                        var value_temp = index==0 ? titleLines[0] : bodyLines[parseInt(event.currentTarget.id)][0].split(':')[0]
                                                        self.chart_filter_label.push(value_temp);
                                                        filter_array.push({
                                                            name: dimension.text,
                                                            column: dimension.value,
                                                            value: value_temp
                                                        })
                                                    })
                                                    self.$emit('exploreChart', {
                                                        chart_selected: self.chart_selected,
                                                        chart_filters: filter_array
                                                    });
                                                });
                                            });
                                        }

                                        let btnExploreAll = tooltipEl.querySelector('.btn-explore-all');

                                        // if chart filter applied on any other chart, disable explore for this current chart
                                        btnExploreAll.disabled = true;
                                        if (!self.applied_chart_filter.if_chart_filter_applied){ 
                                            btnExploreAll.disabled = false;
                                        } else { 
                                            if(self.if_chart_filter_applied && self.chart_filter_label[0] == titleLines[0]){
                                                btnExploreAll.disabled = false;
                                            }
                                        }

                                        if (self.if_chart_filter_applied){
                                            btnExploreAll.addEventListener('click', () => {
                                                self.if_chart_filter_applied = false;
                                                self.chart_filter_label = [];
                                                self.$emit('exploreChart', {
                                                    chart_selected: null,
                                                    chart_filters: []
                                                });
                                            });
                                        } else {
                                            btnExploreAll.addEventListener('click', () => {
                                                self.if_chart_filter_applied = true;
                                                self.chart_filter_label = [titleLines[0]];

                                                self.$emit('exploreChart', {
                                                    chart_selected: self.chart_selected,
                                                    chart_filters: [{
                                                        name: self.chart_selected.group_by[0].text,
                                                        column: self.chart_selected.group_by[0].value,
                                                        value: titleLines[0]
                                                    }]
                                                });
                                            });
                                        }
                                    } else { // benchmark chart tooltip
                                        innerHtml += '</tbody>';
                                        let tableRoot = tooltipEl.querySelector('table');
                                        tableRoot.innerHTML = innerHtml;
                                    }
                                }
                                
                                // Position tooltip using mixin method
                                self.positionTooltipOnSide(tooltipEl, context, dataIndex);
                            }
                        },
                        datalabels: {
                            anchor: function(context) {
                                const chart = context.chart;
                                const dataIndex = context.dataIndex;
                                const total = chart.data.datasets.reduce((sum, ds) => {
                                    const v = ds.data[dataIndex];
                                    return sum + (typeof v === 'number' ? v : 0);
                                }, 0);
                                return total >= 0 ? 'end' : 'start';
                            },
                            align: function(context) {
                                const chart = context.chart;
                                const dataIndex = context.dataIndex;
                                const total = chart.data.datasets.reduce((sum, ds) => {
                                    const v = ds.data[dataIndex];
                                    return sum + (typeof v === 'number' ? v : 0);
                                }, 0);
                                return total >= 0 ? 'end' : 'start';
                            },
                            offset: -2, // Reduce space between label and end of bar
                            color: function(context) {
                                const baseColor = '#5f687c';
                                // If labels are always shown, return full opacity
                                if (self.if_show_label) {
                                    return baseColor;
                                }
                                // For hover labels, apply animated opacity
                                if (self.hoveredIndex !== null && context.dataIndex === self.hoveredIndex) {
                                    return `rgba(17, 25, 33, ${self.hoverOpacity})`;
                                }
                                
                                // For large/x-large charts when toggle is OFF, use metric color for top dataset
                                const chartSize = self.chart_selected?.size || 'small';
                                const numDatasets = Object.keys(self.chart_selected.data).length;
                                const isTopDataset = context.datasetIndex === numDatasets - 1;
                                
                                if ((chartSize === 'large' || chartSize === 'x-large') && isTopDataset) {
                                    // Top dataset should use metric color
                                    return self.getColorByLetter(self.color, 'A');
                                }
                                
                                return baseColor;
                            },
                            display: function(context) {
                                // Only show labels on top dataset (last dataset index) for stacked charts
                                const isTopDataset = context.datasetIndex == Object.keys(self.chart_selected.data).length - 1;
                                if (!isTopDataset) {
                                    return false;
                                }
                                
                                // If toggle is explicitly ON, show labels on top dataset
                                if (self.if_show_label === true) {
                                    const meta = context.chart.getDatasetMeta(context.datasetIndex);
                                    return !meta || !meta.hidden;
                                }
                                
                                // If toggle is explicitly OFF, never show labels (user has toggled off)
                                if (self.if_show_label === false) {
                                    return false;
                                }
                                
                                // When toggle is not explicitly set, apply size-based rules
                                // For large/x-large, labels are shown by default (handled by parent component)
                                // For small/medium, don't show labels
                                const chartSize = self.size || self.chart_selected?.size || 'small';
                                if (chartSize === 'large' || chartSize === 'x-large') {
                                    return true;
                                }
                                
                                // Don't show datalabels when hovering - sideLabelsPlugin handles it
                                // This prevents labels from appearing on top of bars when hovering
                                return false;
                            },
                            font: {
                                weight: 'regular',
                                size: this.font_size,
                            },
                            formatter: function(value, context) {
                                // Only format labels when if_show_label is true or show_all_data_labels is true
                                // Hover labels are handled by sideLabelsPlugin
                                if (!self.chart_selected.show_all_data_labels && !self.if_show_label){
                                    return null;
                                }
                                if (context.datasetIndex == Object.keys(self.chart_selected.data).length - 1){
                                    var label;
                                    var value_temp = self.total_values[context.dataIndex];
                                    if(self.format_type == 'money') {
                                        if(self.show_compressed_numbers && value_temp >= 1000) {
                                            label = self.currency_abbr(value_temp);
                                        } else if(self.max_value >= 10) {
                                            label = self.currency_with_zero(value_temp);
                                        } else {
                                            label = self.currency_with_decimal(value_temp);
                                        }
                                    } else if(self.format_type == 'number') {
                                        if(self.show_compressed_numbers && value_temp >= 1000) {
                                            label = self.num_abbr(value_temp);
                                        } else if(self.max_value >= 10) {
                                            label = self.number_with_zero(value_temp);
                                        } else {
                                            label = self.number_with_decimal(value_temp);
                                        }
                                    } else if(self.format_type == 'percent') {
                                        label = self.percentage(value_temp);
                                    }

                                    return label;
                                } else {
                                    return null;
                                }
                            },
                        },
                        stacked100: { enable: this.if_hundred_percent, replaceTooltipLabel: false }
                    },
                    title: {
                        display:false,
                    },
                }
           };
           return config;
       }
   }
});
</script>
